import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Car,
  Plus,
  Search,
  QrCode,
  User,
  Phone,
  Clock,
  CheckCircle,
  MoreVertical,
  FileSearch,
  Trash2,
  Edit,
  RefreshCw,
  DollarSign,
  Calendar,
  Save,
  X,
  Bell,
  CreditCard,
  Eye,
  Mail,
  Printer,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import AgreementPdfViewer from '@/components/AgreementPdfViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get current local datetime in ISO format for datetime-local input
const getLocalDateTimeString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function LoanCars() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Fleet State
  const [loanCars, setLoanCars] = useState([]);
  const [stats, setStats] = useState({ total: 0, active_count: 0, returned_count: 0 });
  const [fleetLoading, setFleetLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Create/Edit Dialog - New Pickup
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    loan_car_rego: '',
    pickup_datetime: '',
    expected_return_datetime: '',
    notes: ''
  });
  
  // Fleet Car Management (Super Admin and Service Manager)
  const [fleetDialogOpen, setFleetDialogOpen] = useState(false);
  const [fleetCars, setFleetCars] = useState([]);
  const [fleetCarsLoading, setFleetCarsLoading] = useState(false);
  const [newFleetCar, setNewFleetCar] = useState({ rego: '', vehicle_name: '', make: '', model: '', color: '' });
  const [editingFleetCar, setEditingFleetCar] = useState(null);
  
  // Inline Edit State for Drop Off
  const [editingDropOff, setEditingDropOff] = useState(null);
  const [dropOffValue, setDropOffValue] = useState('');
  
  // QR Dialog
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  
  // Fine Lookup
  const [fineLookupOpen, setFineLookupOpen] = useState(false);
  const [fineDate, setFineDate] = useState('');
  const [fineTime, setFineTime] = useState('');
  const [fineRego, setFineRego] = useState('');
  const [fineResults, setFineResults] = useState([]);
  const [fineLoading, setFineLoading] = useState(false);
  
  // Date Filter State
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pending Submissions State
  const [submissions, setSubmissions] = useState([]);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [viewSubmissionOpen, setViewSubmissionOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // License Viewer State
  const [licenseViewerOpen, setLicenseViewerOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementRecord, setAgreementRecord] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          
          if (!['admin', 'super_admin', 'service_manager', 'floor_manager'].includes(userData.role)) {
            navigate('/');
            return;
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const fetchLoanCars = useCallback(async () => {
    setFleetLoading(true);
    try {
      let url = `${BACKEND_URL}/api/loan-cars?page=1&page_size=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLoanCars(data.records || []);
        setStats({
          total: data.total || 0,
          active_count: data.active_count || 0,
          returned_count: data.returned_count || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch loan cars:', error);
      toast.error('Failed to fetch loan car records');
    } finally {
      setFleetLoading(false);
    }
  }, [statusFilter, searchQuery, dateFrom, dateTo]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/submissions?status=pending_review`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
        setSubmissionsCount(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    }
  }, []);

  // Fleet Car Management Functions
  const fetchFleetCars = useCallback(async () => {
    setFleetCarsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/fleet`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFleetCars(data.fleet_cars || []);
      }
    } catch (error) {
      console.error('Failed to fetch fleet cars:', error);
    } finally {
      setFleetCarsLoading(false);
    }
  }, []);

  const handleAddFleetCar = async () => {
    if (!newFleetCar.rego.trim()) {
      toast.error('Please enter the car registration');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/fleet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newFleetCar)
      });

      if (response.ok) {
        toast.success('Fleet car added');
        setNewFleetCar({ rego: '', vehicle_name: '', make: '', model: '', color: '' });
        fetchFleetCars();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add fleet car');
      }
    } catch (error) {
      console.error('Failed to add fleet car:', error);
      toast.error('Failed to add fleet car');
    }
  };

  const handleDeleteFleetCar = async (fleetId) => {
    if (!window.confirm('Delete this fleet car?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/fleet/${fleetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Fleet car deleted');
        fetchFleetCars();
      } else {
        toast.error('Failed to delete fleet car');
      }
    } catch (error) {
      console.error('Failed to delete fleet car:', error);
      toast.error('Failed to delete fleet car');
    }
  };

  useEffect(() => {
    if (user) {
      fetchLoanCars();
      fetchSubmissions();
      if (user.role === 'super_admin') {
        fetchFleetCars();
      }
    }
  }, [user, fetchLoanCars, fetchSubmissions, fetchFleetCars]);

  const handleCreateOrUpdate = async () => {
    if (!formData.customer_name || !formData.customer_phone || !formData.loan_car_rego || !formData.pickup_datetime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingRecord 
        ? `${BACKEND_URL}/api/loan-cars/${editingRecord.loan_id}`
        : `${BACKEND_URL}/api/loan-cars`;
      
      const response = await fetch(url, {
        method: editingRecord ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingRecord ? 'Record updated' : 'New pickup created');
        setCreateDialogOpen(false);
        setEditingRecord(null);
        resetForm();
        // Add small delay to ensure DB has committed, then refresh
        setTimeout(() => {
          fetchLoanCars();
        }, 300);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Operation failed');
      }
    } catch (error) {
      console.error('Failed to save record:', error);
      toast.error('Failed to save record');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      loan_car_rego: '',
      pickup_datetime: getLocalDateTimeString(),
      expected_return_datetime: '',
      notes: ''
    });
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      customer_name: record.customer_name || '',
      customer_phone: record.customer_phone || '',
      loan_car_rego: record.loan_car_rego || '',
      pickup_datetime: record.pickup_datetime ? record.pickup_datetime.slice(0, 16) : '',
      expected_return_datetime: record.expected_return_datetime ? record.expected_return_datetime.slice(0, 16) : '',
      notes: record.notes || ''
    });
    setCreateDialogOpen(true);
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm('Delete this loan car record?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/${loanId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Record deleted');
        fetchLoanCars();
      } else {
        toast.error('Failed to delete record');
      }
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete record');
    }
  };

  // Inline edit for drop off date/time
  const startEditDropOff = (record) => {
    setEditingDropOff(record.loan_id);
    // If there's an expected return, use that, otherwise current local time
    if (record.expected_return_datetime) {
      setDropOffValue(record.expected_return_datetime.slice(0, 16));
    } else {
      setDropOffValue(getLocalDateTimeString());
    }
  };

  const cancelEditDropOff = () => {
    setEditingDropOff(null);
    setDropOffValue('');
  };

  const saveDropOff = async (loanId, markAsReturned = false) => {
    if (!dropOffValue) {
      toast.error('Please select a drop off date/time');
      return;
    }

    try {
      // First update the expected_return_datetime
      const updateResponse = await fetch(`${BACKEND_URL}/api/loan-cars/${loanId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          expected_return_datetime: dropOffValue
        })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update');
      }

      // If marking as returned, also call the return endpoint
      if (markAsReturned) {
        const returnResponse = await fetch(`${BACKEND_URL}/api/loan-cars/${loanId}/return?return_datetime=${dropOffValue}:00`, {
          method: 'POST',
          credentials: 'include'
        });

        if (!returnResponse.ok) {
          throw new Error('Failed to mark as returned');
        }
        toast.success('Loan car returned');
      } else {
        toast.success('Drop off time updated');
      }

      setEditingDropOff(null);
      setDropOffValue('');
      fetchLoanCars();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update drop off time');
    }
  };

  const fetchQrCode = async (loanId) => {
    setQrLoading(true);
    setQrDialogOpen(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/${loanId}/qr`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setQrData(data);
      } else {
        toast.error('Failed to generate QR code');
      }
    } catch (error) {
      console.error('Failed to fetch QR:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const handleFineLookup = async () => {
    if (!fineDate) {
      toast.error('Please enter the offence date');
      return;
    }

    setFineLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/fine-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          offence_date: fineDate,
          offence_time: fineTime || null,
          rego: fineRego || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFineResults(data.matches || []);
        if (data.matches.length === 0) {
          toast.info('No matching loan records found for this date/time');
        }
      } else {
        toast.error('Fine lookup failed');
      }
    } catch (error) {
      console.error('Fine lookup failed:', error);
      toast.error('Fine lookup failed');
    } finally {
      setFineLoading(false);
    }
  };

  // View license image
  const viewLicense = (record) => {
    setSelectedLicense({
      customer_name: record.customer_name,
      license_number: record.license_number,
      license_image_url: record.license_image_url,
      loan_car_rego: record.loan_car_rego,
      vehicle_rego: record.vehicle_rego
    });
    setLicenseViewerOpen(true);
  };


  const viewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setViewSubmissionOpen(true);
  };

  const markAsReviewed = async (submissionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/submissions/${submissionId}/review`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Submission marked as reviewed');
        setViewSubmissionOpen(false);
        fetchSubmissions();
        fetchLoanCars();
      } else {
        toast.error('Failed to update submission');
      }
    } catch (error) {
      console.error('Failed to review submission:', error);
      toast.error('Failed to update submission');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="h-3 w-3 mr-1" /> On Loan</Badge>;
      case 'returned':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Returned</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" /> Pending Pickup</Badge>;
      case 'pending_details':
        return <Badge className="bg-orange-100 text-orange-700"><Bell className="h-3 w-3 mr-1" /> Awaiting Details</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="loan-cars-container">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-['Chivo'] font-bold text-3xl sm:text-4xl text-slate-900 mb-2">
              Loan Cars
            </h1>
            <p className="text-slate-500 text-base">
              Track loan vehicle pickups, returns, and fine allocations
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'super_admin' && (
              <Button
                variant="outline"
                onClick={() => {
                  fetchFleetCars();
                  setFleetDialogOpen(true);
                }}
                data-testid="manage-fleet-btn"
              >
                <Car className="h-4 w-4 mr-2" />
                Manage Fleet
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/loan-cars/qr-print')}
              data-testid="print-qr-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print QR Code
            </Button>
            <Button
              variant="outline"
              onClick={() => setFineLookupOpen(true)}
              data-testid="fine-lookup-btn"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Fine Lookup
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setEditingRecord(null);
                setCreateDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-pickup-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Pickup
            </Button>
          </div>
        </div>

        {/* Pending Submissions Banner */}
        {submissions.length > 0 && (
          <Card className="mb-6 p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-800">
                    {submissions.length} Customer Submission{submissions.length > 1 ? 's' : ''} Pending Review
                  </h3>
                  <p className="text-sm text-amber-600">
                    Customers have submitted their details via QR code
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              {submissions.slice(0, 3).map((sub) => (
                <div 
                  key={sub.submission_id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{sub.customer_name}</p>
                      <p className="text-sm text-slate-500">
                        {sub.loan_car_rego} • {sub.customer_phone}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {new Date(sub.submitted_at).toLocaleTimeString()}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewSubmission(sub)}
                      data-testid={`view-submission-${sub.submission_id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              ))}
              {submissions.length > 3 && (
                <p className="text-sm text-amber-600 text-center">
                  + {submissions.length - 3} more submissions
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
              <Car className="h-6 w-6 text-slate-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-500">Total Records</div>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{stats.active_count}</div>
              <div className="text-sm text-slate-500">Currently On Loan</div>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.returned_count}</div>
              <div className="text-sm text-slate-500">Returned</div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Customer, phone, or rego..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="loan-search-input"
                />
              </div>
            </div>
            <div className="w-[150px]">
              <Label className="text-xs text-slate-500 mb-1 block">Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-filter"
              />
            </div>
            <div className="w-[150px]">
              <Label className="text-xs text-slate-500 mb-1 block">Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-filter"
              />
            </div>
            <div className="w-[150px]">
              <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending Pickup</SelectItem>
                  <SelectItem value="active">On Loan</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchLoanCars} disabled={fleetLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${fleetLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {(dateFrom || dateTo || statusFilter || searchQuery) && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setStatusFilter('');
                  setSearchQuery('');
                }}
                className="text-slate-500"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Loan Records Table */}
        <Card className="p-0 overflow-hidden">
          {loanCars.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Loan Car Rego</TableHead>
                    <TableHead>Customer Name</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Pickup Date/Time</TableHead>
                    <TableHead>Drop Off Date/Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loanCars.map((record) => (
                    <TableRow key={record.loan_id}>
                      <TableCell className="font-mono font-semibold text-slate-900">
                        {record.loan_car_rego || <span className="text-slate-400 font-normal italic">Not assigned</span>}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          {record.customer_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-400" />
                          {record.customer_phone}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDateTime(record.pickup_datetime)}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {editingDropOff === record.loan_id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="datetime-local"
                              value={dropOffValue}
                              onChange={(e) => setDropOffValue(e.target.value)}
                              className="w-48 h-8 text-sm"
                              data-testid={`dropoff-input-${record.loan_id}`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-green-600"
                              onClick={() => saveDropOff(record.loan_id, false)}
                              title="Save drop off time"
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            {record.status === 'active' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-blue-600"
                                onClick={() => saveDropOff(record.loan_id, true)}
                                title="Save & Mark as Returned"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-slate-400"
                              onClick={cancelEditDropOff}
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className={`flex items-center gap-2 ${record.status === 'active' ? 'cursor-pointer hover:text-blue-600' : ''}`}
                            onClick={() => record.status === 'active' && startEditDropOff(record)}
                            title={record.status === 'active' ? 'Click to edit drop off time' : ''}
                          >
                            <Calendar className="h-4 w-4 text-slate-400" />
                            {record.actual_return_datetime 
                              ? formatDateTime(record.actual_return_datetime)
                              : record.expected_return_datetime 
                                ? <span className="text-amber-600">{formatDateTime(record.expected_return_datetime)} (expected)</span>
                                : <span className="text-slate-400">{record.status === 'active' ? 'Click to set' : '-'}</span>
                            }
                            {record.status === 'active' && (
                              <Edit className="h-3 w-3 text-slate-300" />
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(record.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`actions-${record.loan_id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {record.license_image_url && (
                              <DropdownMenuItem
                                onClick={() => viewLicense(record)}
                                data-testid={`view-license-${record.loan_id}`}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View License
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => { setAgreementRecord(record); setAgreementOpen(true); }}
                              data-testid={`view-agreement-${record.loan_id}`}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              View Signed Agreement
                            </DropdownMenuItem>
                            {record.status === 'active' && (
                              <DropdownMenuItem
                                onClick={() => startEditDropOff(record)}
                                data-testid={`update-dropoff-${record.loan_id}`}
                              >
                                <Calendar className="h-4 w-4 mr-2" />
                                Update Drop Off
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => fetchQrCode(record.loan_id)}
                              data-testid={`qr-${record.loan_id}`}
                            >
                              <QrCode className="h-4 w-4 mr-2" />
                              View QR Code
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(record)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Record
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(record.loan_id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Car className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-lg text-slate-700 mb-2">No Loan Car Records</h3>
              <p className="text-slate-500 mb-4">
                Start tracking loan cars by creating your first pickup.
              </p>
              <Button 
                onClick={() => {
                  resetForm();
                  setCreateDialogOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Pickup
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Create/Edit Dialog - New Pickup */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingRecord ? 'Edit Loan Car Record' : 'New Loan Car Pickup'}
            </DialogTitle>
            <DialogDescription>
              {editingRecord ? 'Update the loan car record details.' : 'Record a new loan car pickup for a customer.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="input-customer-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="0412 345 678"
                  data-testid="input-customer-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Loan Car Rego *</Label>
              <Input
                value={formData.loan_car_rego}
                onChange={(e) => setFormData({ ...formData, loan_car_rego: e.target.value.toUpperCase() })}
                placeholder="ABC123"
                className="font-mono"
                data-testid="input-loan-rego"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pickup Date/Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.pickup_datetime}
                  onChange={(e) => setFormData({ ...formData, pickup_datetime: e.target.value })}
                  data-testid="input-pickup"
                />
              </div>
              <div className="space-y-2">
                <Label>Drop Off Date/Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.expected_return_datetime}
                  onChange={(e) => setFormData({ ...formData, expected_return_datetime: e.target.value })}
                  data-testid="input-dropoff"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
                data-testid="input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrUpdate} className="bg-blue-600 hover:bg-blue-700" data-testid="save-pickup-btn">
              {editingRecord ? 'Update Record' : 'Create Pickup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Loan Car QR Code</DialogTitle>
            <DialogDescription>
              Scan this QR code to access the license upload form.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {qrLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
              </div>
            ) : qrData ? (
              <div className="text-center">
                <img
                  src={qrData.qr_image}
                  alt="QR Code"
                  className="mx-auto w-64 h-64 border rounded-lg"
                />
                <p className="mt-4 text-sm text-slate-600">
                  Scan this QR code to access the license upload form.
                </p>
                <p className="mt-2 text-xs text-slate-400 break-all">
                  {qrData.form_url}
                </p>
              </div>
            ) : (
              <p className="text-center text-slate-500">Failed to load QR code</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQrDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fine Lookup Dialog */}
      <Dialog open={fineLookupOpen} onOpenChange={setFineLookupOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              <FileSearch className="h-5 w-5 inline mr-2" />
              Fine Customer Lookup
            </DialogTitle>
            <DialogDescription>
              Enter the date/time from a fine notice to find which customer had the loan car.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Offence Date *</Label>
                <Input
                  type="date"
                  value={fineDate}
                  onChange={(e) => setFineDate(e.target.value)}
                  data-testid="fine-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Offence Time</Label>
                <Input
                  type="time"
                  value={fineTime}
                  onChange={(e) => setFineTime(e.target.value)}
                  data-testid="fine-time-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Loan Car Rego (optional)</Label>
              <Input
                value={fineRego}
                onChange={(e) => setFineRego(e.target.value.toUpperCase())}
                placeholder="Leave blank to search all"
                className="font-mono"
                data-testid="fine-rego-input"
              />
            </div>

            <Button onClick={handleFineLookup} disabled={fineLoading} className="w-full" data-testid="fine-search-btn">
              {fineLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Search Records
            </Button>

            {/* Results */}
            {fineResults.length > 0 && (
              <div className="mt-4 space-y-3">
                <h4 className="font-semibold text-slate-900">Matching Records:</h4>
                {fineResults.map((result, idx) => (
                  <Card key={idx} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{result.customer_name}</p>
                        <p className="text-sm text-slate-600">
                          <Phone className="h-3 w-3 inline mr-1" />
                          {result.customer_phone}
                        </p>
                        <p className="text-sm text-slate-600">
                          <Car className="h-3 w-3 inline mr-1" />
                          Loan Car: {result.loan_car_rego}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Pickup: {formatDateTime(result.pickup_datetime)}
                          {result.actual_return_datetime && (
                            <> | Return: {formatDateTime(result.actual_return_datetime)}</>
                          )}
                        </p>
                      </div>
                      <Badge
                        className={
                          result.match_confidence === 'high'
                            ? 'bg-green-100 text-green-700'
                            : result.match_confidence === 'medium'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-700'
                        }
                      >
                        {result.match_confidence} confidence
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setFineLookupOpen(false);
              setFineResults([]);
              setFineDate('');
              setFineTime('');
              setFineRego('');
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={viewSubmissionOpen} onOpenChange={setViewSubmissionOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              <User className="h-5 w-5 inline mr-2" />
              Customer Submission Review
            </DialogTitle>
            <DialogDescription>
              Review the customer's submitted details and license information
            </DialogDescription>
          </DialogHeader>

          {selectedSubmission && (
            <div className="space-y-4 py-4">
              {/* Loan Car Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Car className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-600">Loan Car</p>
                    <p className="text-xl font-bold font-mono text-blue-900">{selectedSubmission.loan_car_rego}</p>
                  </div>
                </div>
              </div>

              {/* Customer Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <User className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Customer Name</p>
                    <p className="font-medium text-slate-900">{selectedSubmission.customer_name}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Phone Number</p>
                    <p className="font-medium text-slate-900">{selectedSubmission.customer_phone}</p>
                  </div>
                </div>
                
                {selectedSubmission.customer_email && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{selectedSubmission.customer_email}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CreditCard className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">License Number</p>
                    <p className="font-medium text-slate-900">{selectedSubmission.license_number}</p>
                  </div>
                </div>
              </div>

              {/* License Image */}
              {selectedSubmission.license_image_url && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">License Photo</p>
                  <img 
                    src={`${BACKEND_URL}${selectedSubmission.license_image_url}`}
                    alt="License" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                </div>
              )}

              {/* Submission Time */}
              <div className="text-sm text-slate-500 text-center">
                Submitted: {new Date(selectedSubmission.submitted_at).toLocaleString()}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewSubmissionOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => selectedSubmission && markAsReviewed(selectedSubmission.submission_id)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark as Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* License Viewer Dialog */}
      <Dialog open={licenseViewerOpen} onOpenChange={setLicenseViewerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Driver's License
            </DialogTitle>
            <DialogDescription>
              License details for loan car pickup
            </DialogDescription>
          </DialogHeader>

          {selectedLicense && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Customer</p>
                  <p className="font-medium text-slate-900">{selectedLicense.customer_name}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">License Number</p>
                  <p className="font-medium text-slate-900 font-mono">{selectedLicense.license_number || 'N/A'}</p>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600">Loan Car</p>
                  <p className="font-bold text-blue-900 font-mono">{selectedLicense.loan_car_rego || 'Not assigned'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Customer Vehicle</p>
                  <p className="font-medium text-slate-900 font-mono">{selectedLicense.vehicle_rego || 'N/A'}</p>
                </div>
              </div>

              {/* License Image */}
              {selectedLicense.license_image_url ? (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">License Photo</p>
                  <div className="relative group">
                    <img 
                      src={`${BACKEND_URL}${selectedLicense.license_image_url}`}
                      alt="Driver's License" 
                      className="w-full h-auto max-h-80 object-contain rounded-lg border bg-slate-100 cursor-pointer"
                      onClick={() => window.open(`${BACKEND_URL}${selectedLicense.license_image_url}`, '_blank')}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg flex items-center justify-center">
                      <span className="hidden group-hover:block text-white bg-black/50 px-3 py-1 rounded text-sm">
                        Click to open full size
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 bg-slate-50 rounded-lg text-center">
                  <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No license photo uploaded</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setLicenseViewerOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AgreementPdfViewer
        open={agreementOpen}
        onOpenChange={setAgreementOpen}
        record={agreementRecord}
        backendUrl={BACKEND_URL}
      />

      {/* Fleet Management Dialog */}
      <Dialog open={fleetDialogOpen} onOpenChange={setFleetDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Manage Workshop Fleet Cars
            </DialogTitle>
            <DialogDescription>
              Add loan cars to your workshop fleet. These cars will appear as options when customers scan the QR code.
            </DialogDescription>
          </DialogHeader>

          {/* Add New Fleet Car */}
          <div className="p-4 bg-slate-50 rounded-lg border mb-4">
            <h4 className="font-medium text-slate-700 mb-3">Add New Fleet Car</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Registration *</Label>
                <Input
                  value={newFleetCar.rego}
                  onChange={(e) => setNewFleetCar({ ...newFleetCar, rego: e.target.value.toUpperCase() })}
                  placeholder="e.g., ABC123"
                  data-testid="fleet-rego-input"
                />
              </div>
              <div>
                <Label className="text-xs">Vehicle Name *</Label>
                <Input
                  value={newFleetCar.vehicle_name}
                  onChange={(e) => setNewFleetCar({ ...newFleetCar, vehicle_name: e.target.value })}
                  placeholder="e.g., Loan Car 1"
                  data-testid="fleet-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Make</Label>
                <Input
                  value={newFleetCar.make}
                  onChange={(e) => setNewFleetCar({ ...newFleetCar, make: e.target.value })}
                  placeholder="e.g., Toyota"
                />
              </div>
              <div>
                <Label className="text-xs">Model</Label>
                <Input
                  value={newFleetCar.model}
                  onChange={(e) => setNewFleetCar({ ...newFleetCar, model: e.target.value })}
                  placeholder="e.g., Corolla"
                />
              </div>
            </div>
            <Button onClick={handleAddFleetCar} className="mt-3" data-testid="add-fleet-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Car
            </Button>
          </div>

          {/* Fleet Cars List */}
          <div className="space-y-2">
            <h4 className="font-medium text-slate-700">Current Fleet ({fleetCars.length} cars)</h4>
            {fleetCarsLoading ? (
              <div className="text-center py-4">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto text-slate-400" />
              </div>
            ) : fleetCars.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No fleet cars added yet</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {fleetCars.map((car) => (
                  <div key={car.fleet_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Badge variant={car.status === 'available' ? 'default' : car.status === 'on_loan' ? 'secondary' : 'outline'}>
                        {car.rego}
                      </Badge>
                      <span className="font-medium text-slate-800">
                        {car.vehicle_name || 'Unnamed'}
                      </span>
                      <span className="text-sm text-slate-500">
                        {[car.make, car.model].filter(Boolean).join(' ') || ''}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {car.status === 'available' ? 'Available' : car.status === 'on_loan' ? 'On Loan' : car.status}
                      </Badge>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteFleetCar(car.fleet_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFleetDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
